import {useMemo} from 'react'
import {Button, Empty, Space, Table, Typography} from 'antd'
import {CopyOutlined, DeleteOutlined, EditOutlined, ExportOutlined} from '@ant-design/icons'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import RecordsCount from '../../common/RecordsCount'
import TableActions from '../../common/TableActions'
import {useIntl} from 'react-intl'

const ConnectorTable = ({
  dataSource,
  pagination = false,
  getConnectorListData = null,
  onEdit = null,
  onDelete = null,
  onCloneSvg = null,
  onDownloadSvg = null,
  onShowLocation = null,
}) => {
  const intl = useIntl()

  const columns = useMemo(
    () => [
      {
        title: (
          <EllipsisTooltip className='ellipsis'>
            {intl.formatMessage({ id: 'connectors.name' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'name',
        sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {text || '-'}
          </EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className='ellipsis'>
            {intl.formatMessage({ id: 'connectors.category' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'category',
        width: '12%',
        sorter: (a, b) => (a.category || '').localeCompare(b.category || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {text || '-'}
          </EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className='ellipsis'>
            {intl.formatMessage({ id: 'connectors.description' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'description',
        sorter: (a, b) => (a.description || '').localeCompare(b.description || ''),
        render: (text) => (
            <EllipsisTooltip className="multiline-ellipsis" lineClamp={2}>
              {text || '-'}
            </EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className='ellipsis'>
            {intl.formatMessage({ id: 'connectors.device' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'device_display',
        width: '9%',
        sorter: (a, b) => (a.device_display || '').localeCompare(b.device_display || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {text || '-'}
          </EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className='ellipsis'>
            {intl.formatMessage({ id: 'connectors.alias' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'alias',
        width: '8%',
        sorter: (a, b) => (a.alias || '').localeCompare(b.alias || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {text || '-'}
          </EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className='ellipsis'>
            {intl.formatMessage({ id: 'connectors.color' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'color',
        width: '6%',
        sorter: (a, b) => (a.color || '').localeCompare(b.color || ''),
        render: (text) => (
          <Typography.Text>{text || '-'}</Typography.Text>
        ),
      },
      {
        title: (
          <EllipsisTooltip className='ellipsis'>
            {intl.formatMessage({ id: 'connectors.svg' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'has_svg',
        width: '6%',
        align: 'center',
        filters: [
          { text: intl.formatMessage({ id: 'connectors.yes' }), value: 'yes' },
          { text: intl.formatMessage({ id: 'connectors.no' }), value: 'no' },
        ],
        onFilter: (value, record) => {
          const has =
            record.svg !== null && record.svg !== undefined && record.svg !== ''
          return value === 'yes' ? has : !has
        },
        render: (_, record) => {
          const has = record.svg !== null && record.svg !== undefined && record.svg !== ''
          return (
            <Typography.Text>{has ? intl.formatMessage({ id: 'connectors.yes' }) : intl.formatMessage({ id: 'connectors.no' })}</Typography.Text>
          )
        },
      },
      {
        title: (
          <EllipsisTooltip className='ellipsis'>
            {intl.formatMessage({ id: 'connectors.cavities' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'cavities',
        width: '9%',
        sorter: (a, b) => String(a.cavities || '').localeCompare(String(b.cavities || '')),
        render: (text) => (
          <Typography.Text>{text || '-'}</Typography.Text>
        ),
      },
      {
        title: (
          <EllipsisTooltip className='ellipsis'>
            {intl.formatMessage({ id: 'connectors.location' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'has_location',
        width: '9%',
        align: 'center',
        filters: [
          { text: intl.formatMessage({ id: 'connectors.yes' }), value: 'yes' },
          { text: intl.formatMessage({ id: 'connectors.no' }), value: 'no' },
        ],
        onFilter: (value, record) => {
          const has = record.location?.length > 0
          return value === 'yes' ? has : !has
        },
        render: (_, record) => {
          const has = record.location?.length > 0
          return has ? (
            <Button
              type="link"
              className="link-button"
              onClick={() => onShowLocation?.(record)}
            >
              {intl.formatMessage({ id: 'connectors.yes' })}
            </Button>
          ) : (
            <Typography.Text>{intl.formatMessage({ id: 'connectors.no' })}</Typography.Text>
          )
        },
      },
      {
        title: (
          <EllipsisTooltip className='ellipsis'>
            {intl.formatMessage({ id: 'connectors.action' })}
          </EllipsisTooltip>
        ),
        align: 'right',
        width: 136,
        render: (_, record) => {
          const hasSvg =
            record.svg !== null && record.svg !== undefined && record.svg !== ''
          const actions = [
            { key: 'edit', label: intl.formatMessage({ id: 'connectors.edit' }), icon: <EditOutlined />, onClick: () => onEdit?.(record) },
            { key: 'delete', label: intl.formatMessage({ id: 'connectors.delete' }), icon: <DeleteOutlined />, danger: true, onClick: () => onDelete?.(record) },
            { key: 'clone_svg', label: intl.formatMessage({ id: 'connectors.cloneSvg' }), icon: <CopyOutlined />, onClick: () => onCloneSvg?.(record) },
            ...(hasSvg
              ? [
                  { key: 'export_svg', label: intl.formatMessage({ id: 'connectors.exportSvg' }), icon: <ExportOutlined />, onClick: () => onDownloadSvg?.(record) },
                ]
              : []),
          ]
          return <TableActions actions={actions} />
        },
      },
    ],
    [dataSource, pagination]
  )

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%', verticalAlign: 'top' }}
    >
      <Table
        rowKey={(record) => `connector-${record.id}`}
        columns={columns}
        dataSource={dataSource}
        showSorterTooltip={false}
        tableLayout='fixed'
        scroll={dataSource?.length ? { x: 1360 } : undefined}
        pagination={
          pagination
            ? {
                current: pagination?.currentPage,
                total: pagination?.totalCount,
                pageSize: 10,
                showSizeChanger: false,
                hideOnSinglePage: true,
                showTotal: (total) => <RecordsCount count={total} />,
                onChange: (page) => {
                  const offset = (page - 1) * 10
                  const url = pagination?.nextPageLink
                    ? new URL(pagination?.nextPageLink)
                    : new URL(pagination?.prevPageLink)
                  url.searchParams.set('offset', offset)
                  getConnectorListData(url.toString())
                },
              }
            : false
        }
        locale={{
          emptyText: (
            <div style={{ padding: '40px' }}>
              <Empty description={<RecordsCount count={0} />} />
            </div>
          ),
        }}
      />
      {pagination && pagination?.totalPages <= 1 && pagination?.totalCount > 0 && (
        <Typography.Text>
          <RecordsCount count={pagination?.totalCount} />
        </Typography.Text>
      )}
    </Space>
  )
}

export default ConnectorTable
